<template>
  <form
    autocomplete="off"
    class="patient-basic-edit"
    v-on:submit.prevent="dispatchSubmit"
  >
    <div class="patient-basic-edit__row">
      <input-text
        title="Фамилия"
        required
        v-bind:violations="surnameInputErrors"
        v-letter-capital
        v-model="surname"
      />
      <input-text
        title="Имя"
        required
        v-bind:violations="nameInputErrors"
        v-letter-capital
        v-model="name"
      />
      <input-text
        title="Отчество"
        v-bind:violations="patronymicInputErrors"
        v-letter-capital
        v-model="patronymic"
      />
    </div>
    <input-date
      label="Дата рождения"
      v-bind:violations="birthDateInputErrors"
      v-model="birthDate" />
    <fieldset class="patient-basic-edit__sex">
      <legend>Пол</legend>
      <label class="patient-basic-edit__sex__radio">
        <input name="sex" type="radio" v-bind:value="$options.sexMale" v-model="sexId" />
        <span>Мужской</span>
      </label>
      <label class="patient-basic-edit__sex__radio">
        <input name="sex" type="radio" v-bind:value="$options.sexFemale" v-model="sexId" />
        <span>Женский</span>
      </label>
      <label class="patient-basic-edit__sex__radio">
        <input name="sex" type="radio" v-bind:value="$options.sexNone" v-model="sexId" />
        <span>Не указан</span>
      </label>
    </fieldset>
    <div class="patient-basic-edit__row">
      <input-text
        title="Номер мед. карты"
        v-bind:violations="medicalCardNumberInputErrors"
        v-mask="cardNumberMask"
        v-model="medicalCardNumber"
      />
      <input-date
        label="Дата начала ведения мед. карты"
        v-bind:violations="medicalCardCreatedAtInputErrors"
        v-model="medicalCardCreatedAt" />
    </div>
    <textarea-length-limitation
      title="Место работы"
      placeholder="Место работы пациента"
      rows="2"
      v-bind:violations="workplaceInputErrors"
      v-bind:maxLength="255"
      v-model="workplace"
    />
    <textarea-length-limitation
      placeholder="Примечание"
      rows="5"
      v-bind:maxLength="200"
      v-bind:violations="noteInputErrors"
      v-model="note"
    />
    <div class="patient-basic-edit__labels">
      <input-checkbox
        v-bind:key="id"
        v-bind:title="name"
        v-bind:value="selectedLabels.includes(id)"
        v-for="{ id, name } in labels"
        v-on:input="onChangeLabel(id, $event)"
      />
    </div>
    <div class="patient-basic-edit__buttons">
      <button class="patient-basic-edit__button-cancel" type="button" v-on:click="cancelDispatch">
        Отмена
      </button>
      <button class="patient-basic-edit__button-save" type="submit">Сохранить</button>
    </div>
  </form>
</template>
<script>
import { Medcard } from '@/storage/gateways/PatientGateway';
import dateMixin from '@/mixins/dateMixin';
import formMeaningfulFieldsMixin from '@/mixins/formMeaningfulFieldsMixin';
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputDate from '@/components/InputDate.vue';
import InputText from '@/components/InputText.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import { GOAL_ADD_PATIENT_LABELS } from '@/plugins/metric';
import metricMixin from '@/mixins/metricMixin';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (
  cause,
) => (vm) => (cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Компонент формы создания сотрудника.
 * @type {Vue}
 */
export default {
  sexMale: 1,
  sexFemale: 2,
  sexNone: 3,
  components: {
    InputCheckbox,
    InputDate,
    InputText,
    TextareaLengthLimitation,
  },
  computed: {
    birthDateInputErrors: resolveInputErrors('birth_date'),
    medicalCardCreatedAtInputErrors: resolveInputErrors('medical_card_created_at'),
    medicalCardNumberInputErrors: resolveInputErrors('medical_card_number'),
    nameInputErrors: resolveInputErrors('name'),
    workplaceInputErrors: resolveInputErrors('workplace'),
    noteInputErrors: resolveInputErrors('note'),
    patronymicInputErrors: resolveInputErrors('patronymic'),
    sexInputErrors: resolveInputErrors('sex'),
    surnameInputErrors: resolveInputErrors('surname'),
    /**
     * @description Значимые поля.
     * @type {any}
     */
    meaningfulFields: (vm) => ({
      birthDate: vm.birthDate,
      medicalCardCreatedAt: vm.medicalCardCreatedAt,
      medicalCardNumber: vm.medicalCardNumber,
      name: vm.name,
      note: vm.note,
      patronymic: vm.patronymic,
      selectedLabels: vm.selectedLabels,
      sexId: vm.sexId,
      surname: vm.surname,
    }),
  },
  data() {
    const date = new Date();
    let mouth = `${date.getMonth() + 1}`;
    if (mouth.length === 1) {
      mouth = `0${mouth}`;
    }
    let day = `${date.getDate()}`;
    if (day.length === 1) {
      day = `0${day}`;
    }
    const currentDate = `${date.getFullYear()}-${mouth}-${day}`;
    const medicalCardCreatedAt = (this.medcard.medicalCardCreatedAt || currentDate)
      .split('-')
      .reverse()
      .join('/');
    return {
      /**
       * @description Дата рождения пациента.
       * @type {string}
       */
      birthDate: (this.medcard.birthDate || '').split('-').reverse().join('/'),
      /**
       * @description Дата начала ведения медкарты.
       * @type {string}
       */
      medicalCardCreatedAt,
      /**
       * @description Номер медкарты.
       * @type {string}
       */
      medicalCardNumber: this.medcard.medicalCardNumber || this.defaultMedcardNumber || '',
      /**
       * @description Имя пациента.
       * @type {string}
       */
      name: this.medcard.name || '',
      /**
       * @description Место работы.
       * @type {string}
       */
      workplace: this.medcard.workplace,
      /**
       * @description Примечание.
       * @type {string}
       */
      note: this.medcard.note,
      /**
       * @description Отчество.
       * @type {string}
       */
      patronymic: this.medcard.patronymic || '',
      /**
       * @description Выбранные черты.
       * @type {string}
       */
      selectedLabels: this.medcard.labels.map((item) => item.id) || [],
      /**
       * @description Идентификатор пола.
       * @type {number|string}
       */
      sexId: this.medcard.sexId || '',
      /**
       * @description Фамилия.
       * @type {string}
       */
      surname: this.medcard.surname || '',
    };
  },
  methods: {
    /**
     * @description Обработать изменение списка черт пациента.
     * @param {string} id Идентификатор черты.
     * @param {boolean} isSelected Выбрана.
     * @returns {void}
     */
    onChangeLabel(id, isSelected) {
      if (isSelected) {
        this.selectedLabels.push(id);
        this.reachPatientLabelGoal();
      } else {
        const index = this.selectedLabels.indexOf(id);
        if (index !== -1) {
          this.$delete(this.selectedLabels, index);
        }
      }
      this.$emit('change-labels', { id, isSelected });
    },
    /**
     * @description Достигнута цель - добавление меток пациента.
     * @returns {void}
     */
    reachPatientLabelGoal() {
      this.reachGoalIfNotReach(GOAL_ADD_PATIENT_LABELS);
    },
    /**
     * @description Маска мед. карты.
     * @returns {function}
     */
    cardNumberMask(value) {
      return [value.replace(/[^0-9-—+/a-zA-Zа-яА-ЯёЁ]/, '').substring(0, 20)];
    },
    /**
     * @description Получить заголовок даты рождения.
     * @returns {string}
     */
    getBirthDateTitle() {
      const birthAt = new Date(this.formatDateToISO(this.birthDate));
      const now = new Date();
      const age = new Date(now.getTime() - birthAt.getTime()).getFullYear() - 1970;
      const index = this.getAgePluralIndex(age);
      const pluralizationDictionary = ['год', 'года', 'лет'];
      return `${this.birthDate} (${age} ${pluralizationDictionary[index]})`;
    },
    /**
     * @description Получить индекс для использования во множественном числе.
     * @param {number} age Возраст.
     * @returns {number}
     */
    getAgePluralIndex(age) {
      if (age % 10 === 1 && age % 100 !== 11) {
        return 0;
      }
      return (age % 10 >= 2) && (age % 10 <= 4) && ((age % 100 < 10) || (age % 100 >= 20))
        ? 1
        : 2;
    },
    /**
     * @description Отправить событие подтверждения формы.
     * @returns {void}
     */
    dispatchSubmit() {
      const birthDateTitle = this.birthDate ? this.getBirthDateTitle() : '';
      const medcard = new Medcard(
        this.formatDateToISO(this.birthDate),
        birthDateTitle,
        this.formatDateToISO(this.medicalCardCreatedAt),
        this.medicalCardNumber,
        this.name,
        this.note,
        this.patronymic,
        this.sexId,
        this.surname,
        this.labels.filter((item) => this.selectedLabels.includes(item.id)),
        this.workplace,
      );
      this.$emit('submit', medcard);
    },
    /**
     * @description Отправить событие закрытия формы.
     * @returns {void}
     */
    cancelDispatch() {
      this.$emit('cancel');
    },
  },
  props: {
    /**
     * @description Номер медкарты пациента по умолчанию.
     * @type {string}
     */
    defaultMedcardNumber: { type: String, required: true },
    /**
     * @description Нарушения ограничений.
     * @type {string}
     */
    inputErrors: { type: Object, required: true },
    /**
     * @description Черты пациентов.
     * @type {any[]}
     */
    labels: { type: Array, default: () => [] },
    /**
     * @description Медкарта.
     * @type {Medcard}
     */
    medcard: { type: Medcard, required: true },
  },
  mixins: [dateMixin, formMeaningfulFieldsMixin, metricMixin],
  watch: {},
};
</script>
<style scoped lang="scss">
@use "@/scss/buttons";
.patient-basic-edit {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 25px;
  margin: 37px 0 0 0;
  &__row {
    display: flex;
    flex-direction: row;
    grid-gap: 10px;
    & > * {
      flex: 1;
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    max-width: 438px;
    width: 100%;
    margin: 55px 0 0 auto;
  }
  &__button {
    &-cancel {
      @include buttons.btn-transparent(100%, 56px, 20px);
    }
    &-save {
      @include buttons.btn-blue(100%, 56px, 20px);
    }
  }
  &__labels {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(5, 1fr);
    grid-auto-flow: column;
    row-gap: 10px;
  }
  &__sex {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 0;
    border: 0;
    gap: 10px;
    legend {
      display: contents;
    }
    &__radio > input {
      position: absolute;
      z-index: -1;
      opacity: 0;
    }
    &__radio > span {
      display: inline-flex;
      align-items: center;
      user-select: none;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      cursor: pointer;
    }
    &__radio > span::before {
      content: "";
      display: inline-block;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      flex-grow: 0;
      border: 1px solid #90a0b7;
      border-radius: 50%;
      margin-right: 10px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 50% 50%;
    }
    &__radio > input:not(:disabled):not(:checked) + span:hover::before {
      border: 1px solid #0489e0;
    }
    &__radio > input:checked + span::before {
      border: 1px solid #0489e0;
      background-color: #0489e0;
      box-shadow: inset 0 0 0 6px #fff;
    }
  }
}

@media screen and (max-width: 520px) {
  .patient-basic-edit {
    &__row {
      flex-direction: column;
    }
    &__labels {
      grid-auto-flow: dense;
    }
  }
}
</style>
